import { useCallback, useContext, useMemo, useState } from 'react';
import {
  ProjectAPI,
  ProjectFinancialsApiQueryParams,
  ProjectFinancialsGroupBy,
  ProjectFinancialsDateRange,
  ProjectFinancialsTableRowData,
  ProjectFinancialsTableType,
  ProjectFinancialsZoomLevel,
} from 'api/project';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import { getFinancialsTableRowData } from 'utils/project-financials';

interface ReturnProps {
  isBudgetTableLoading: boolean;
  isHoursTableLoading: boolean;
  budgetTableRange: ProjectFinancialsDateRange;
  hoursTableRange: ProjectFinancialsDateRange;
  budgetRowData: ProjectFinancialsTableRowData[] | undefined;
  hoursRowData: ProjectFinancialsTableRowData[] | undefined;
  budgetTableGroupBy: ProjectFinancialsGroupBy;
  hoursTableGroupBy: ProjectFinancialsGroupBy;
  budgetTableZoomLevel: ProjectFinancialsZoomLevel;
  hoursTableZoomLevel: ProjectFinancialsZoomLevel;
  onBudgetTableGroupByChange: (value: ProjectFinancialsGroupBy) => void;
  onHoursTableGroupByChange: (value: ProjectFinancialsGroupBy) => void;
  onBudgetTableZoomLevelChange: (value: ProjectFinancialsZoomLevel) => void;
  onHoursTableZoomLevelChange: (value: ProjectFinancialsZoomLevel) => void;
}

export const useProjectFinancialsTable = (): ReturnProps => {
  const context = useContext(ProjectFinancialsContext);
  const { project, projectFinancials } = context;

  const rowData: ProjectFinancialsTableRowData[] | undefined = useMemo(
    () =>
      projectFinancials.financialsData.breakdown == null
        ? undefined
        : getFinancialsTableRowData(projectFinancials.financialsData),
    [projectFinancials.financialsData],
  );

  const [isBudgetTableLoading, setIsBudgetTableLoading] = useState(false);
  const [isHoursTableLoading, setIsHoursTableLoading] = useState(false);
  const [budgetTableRange, setBudgetTableRange] =
    useState<ProjectFinancialsDateRange>({
      startDate: projectFinancials.startDate,
      endDate: projectFinancials.endDate,
    });
  const [hoursTableRange, setHoursTableRange] =
    useState<ProjectFinancialsDateRange>({
      startDate: projectFinancials.startDate,
      endDate: projectFinancials.endDate,
    });
  const [budgetRowData, setBudgetRowData] = useState(rowData);
  const [hoursRowData, setHoursRowData] = useState(rowData);
  const [budgetTableGroupBy, setBudgetTableGroupBy] = useState(
    ProjectFinancialsGroupBy.Role,
  );
  const [hoursTableGroupBy, setHoursTableGroupBy] = useState(
    ProjectFinancialsGroupBy.Role,
  );
  const [budgetTableZoomLevel, setBudgetTableZoomLevel] =
    useState<ProjectFinancialsZoomLevel>(ProjectFinancialsZoomLevel.Monthly);

  const [hoursTableZoomLevel, setHoursTableZoomLevel] =
    useState<ProjectFinancialsZoomLevel>(ProjectFinancialsZoomLevel.Monthly);

  const updateBudgetTableData = useCallback(
    ({ groupBy, zoomLevel }: ProjectFinancialsApiQueryParams) => {
      setIsBudgetTableLoading(true);
      ProjectAPI.getProjectFinancialsWithQuery(project.projectId, {
        groupBy,
        zoomLevel,
        tabType: ProjectFinancialsTableType.Budget,
      })
        .then((data) => {
          const { startDate, endDate, financialsData } = data;

          if (startDate != null && endDate != null) {
            setBudgetTableRange({
              startDate,
              endDate,
            });
          }
          if (financialsData != null) {
            setBudgetRowData(getFinancialsTableRowData(financialsData));
          }
        })
        .finally(() => {
          setIsBudgetTableLoading(false);
        });
    },
    [project.projectId],
  );

  const updateHoursTableData = useCallback(
    ({ groupBy, zoomLevel }: ProjectFinancialsApiQueryParams) => {
      setIsHoursTableLoading(true);
      ProjectAPI.getProjectFinancialsWithQuery(project.projectId, {
        groupBy,
        zoomLevel,
        tabType: ProjectFinancialsTableType.Hours,
      })
        .then((data) => {
          const { startDate, endDate, financialsData } = data;

          if (startDate != null && endDate != null) {
            setHoursTableRange({
              startDate,
              endDate,
            });
          }

          if (financialsData != null) {
            setHoursRowData(getFinancialsTableRowData(financialsData));
          }
        })
        .finally(() => {
          setIsHoursTableLoading(false);
        });
    },
    [project.projectId],
  );

  const onBudgetTableZoomLevelChange = useCallback(
    (value: ProjectFinancialsZoomLevel) => {
      setBudgetTableZoomLevel(value);
      updateBudgetTableData({
        groupBy: budgetTableGroupBy,
        zoomLevel: value,
      });
    },
    [budgetTableGroupBy, updateBudgetTableData],
  );

  const onHoursTableZoomLevelChange = useCallback(
    (value: ProjectFinancialsZoomLevel) => {
      setHoursTableZoomLevel(value);
      updateHoursTableData({
        groupBy: hoursTableGroupBy,
        zoomLevel: value,
      });
    },
    [hoursTableGroupBy, updateHoursTableData],
  );

  const onBudgetTableGroupByChange = useCallback(
    (value: ProjectFinancialsGroupBy) => {
      setBudgetTableGroupBy(value);
      updateBudgetTableData({
        groupBy: value,
        zoomLevel: budgetTableZoomLevel,
      });
    },
    [budgetTableZoomLevel, updateBudgetTableData],
  );

  const onHoursTableGroupByChange = useCallback(
    (value: ProjectFinancialsGroupBy) => {
      setHoursTableGroupBy(value);
      updateHoursTableData({
        groupBy: value,
        zoomLevel: hoursTableZoomLevel,
      });
    },
    [hoursTableZoomLevel, updateHoursTableData],
  );

  return {
    isBudgetTableLoading,
    isHoursTableLoading,
    budgetTableRange,
    hoursTableRange,
    budgetRowData,
    hoursRowData,
    budgetTableGroupBy,
    hoursTableGroupBy,
    budgetTableZoomLevel,
    hoursTableZoomLevel,
    onBudgetTableGroupByChange,
    onHoursTableGroupByChange,
    onBudgetTableZoomLevelChange,
    onHoursTableZoomLevelChange,
  };
};
